import type { TokenView } from '@onuw/protocol'
import { type ArtifactId, type MarkId, ROLE_IDS, type RoleId } from '@onuw/roles-data'
import { Fragment } from 'react'
import { Card } from '../../ui/Card'
import { TokenDetails } from '../../ui/Token'
import { RichText, RoleLink } from '../RichText'
import { ROLE_GUIDES } from '../roleGuides'
import styles from './guides.module.css'

export { RoleLink as R } from '../RichText'

/** Roles one per row: the card, its name as a link to its page, and what it does in a line. */
export function RoleList({ roles }: { roles: RoleId[] }) {
  return (
    <div className={styles.roleList}>
      {roles.map((role) => (
        <div key={role} className={styles.roleRow}>
          <Card role={role} width="100%" sizes="64px" />
          <span>
            <span className={styles.roleName}>
              <RoleLink id={role} />
            </span>
            <span className={styles.roleLead}>
              <RichText text={ROLE_GUIDES[role].lead} />
            </span>
          </span>
        </div>
      ))}
    </div>
  )
}

/** A deck in the registry's order: "2 × Werewolf, Seer, Robber", each role a link. */
export function Deck({ roles }: { roles: Partial<Record<RoleId, number>> }) {
  const listed = ROLE_IDS.filter((id) => (roles[id] ?? 0) > 0)
  return (
    <>
      {listed.map((id, i) => (
        <Fragment key={id}>
          {i > 0 && ', '}
          {(roles[id] ?? 0) > 1 && `${roles[id]} × `}
          <RoleLink id={id} />
        </Fragment>
      ))}
    </>
  )
}

const tokenView = (kind: 'mark' | 'artifact', identity: string): TokenView => ({
  id: `guide-${identity}`,
  kind,
  seat: 0,
  identity,
})

/** Marks or artifacts one per row: the medallion, its name and what it does. */
export function TokenList({
  marks,
  artifacts,
}: {
  marks?: readonly MarkId[]
  artifacts?: readonly ArtifactId[]
}) {
  const tokens = [
    ...(marks ?? []).map((id) => tokenView('mark', id)),
    ...(artifacts ?? []).map((id) => tokenView('artifact', id)),
  ]
  return (
    <div className={styles.tokens}>
      {tokens.map((tk) => (
        <TokenDetails key={tk.id} token={tk} />
      ))}
    </div>
  )
}
